import { ArrowDownLeft, ArrowUpRight, Mail, Paperclip } from "lucide-react";
import { DateTime } from "luxon";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";

import { useClaimConversations, useExtractFrontConversation } from "../../api/use-claim-actions";
import { truncate } from "./conversation-utils";

function formatDate(iso: string) {
  return DateTime.fromISO(iso).toFormat("dd/MM/yyyy HH:mm");
}

interface ConversationSelectionModalProps {
  claimId: string;
  open: boolean;
  onClose: () => void;
}

/** Port of emails/ConversationSelectionModal.vue — pick the emails to extract as a PDF. */
export function ConversationSelectionModal({ claimId, open, onClose }: ConversationSelectionModalProps) {
  const { data, isPending: loading } = useClaimConversations(claimId, open);
  const messages = data ?? [];
  const extractMutation = useExtractFrontConversation(claimId);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Pre-check the selection saved at the last extraction (the API checks everything when none was saved).
  useEffect(() => {
    if (open && data) {
      setSelectedIds(new Set(data.filter(({ isSelected }) => isSelected).map(({ messageId }) => messageId)));
    }
  }, [open, data]);

  const allSelected = messages.length > 0 && selectedIds.size === messages.length;
  const someSelected = selectedIds.size > 0 && selectedIds.size < messages.length;

  function toggleAll() {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(messages.map((message) => message.messageId)));
    }
  }

  function toggleMessage(id: string) {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  function extract() {
    if (selectedIds.size === 0) {
      return;
    }
    extractMutation.mutate([...selectedIds], {
      onSuccess: () => onClose(),
    });
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          onClose();
        }
      }}
    >
      <DialogContent className="gap-0 p-0 sm:max-w-4xl">
        <DialogHeader className="border-b px-6 py-4">
          <DialogTitle className="text-lg font-semibold text-gray-900">
            Sélectionner les emails à extraire
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Spinner className="size-8 text-gray-400" />
          </div>
        ) : (
          <div className="max-h-[60vh] overflow-y-auto">
            {/* Select all */}
            <div className="sticky top-0 z-10 flex items-center gap-3 border-b bg-gray-50 px-6 py-3">
              <Checkbox
                checked={allSelected}
                indeterminate={someSelected}
                onCheckedChange={toggleAll}
                aria-label="Tout sélectionner"
              />
              <span className="text-sm text-gray-600">
                {selectedIds.size} / {messages.length} email{messages.length > 1 ? "s" : ""} sélectionné
                {selectedIds.size > 1 ? "s" : ""}
              </span>
            </div>

            {/* Message list */}
            <ul className="divide-y divide-gray-100">
              {messages.map((message) => {
                const isSelected = selectedIds.has(message.messageId);
                return (
                  <li key={message.messageId}>
                    {/* biome-ignore lint/a11y/useSemanticElements: the row hosts a nested checkbox, a <button> cannot */}
                    <div
                      onClick={() => toggleMessage(message.messageId)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          toggleMessage(message.messageId);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      className={cn(
                        "flex cursor-pointer items-start gap-3 px-6 py-3 transition-colors hover:bg-gray-50",
                        isSelected ? "bg-blue-50/50" : "opacity-50",
                      )}
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggleMessage(message.messageId)}
                        onClick={(event) => event.stopPropagation()}
                        className="mt-1 shrink-0"
                        aria-label={message.subject ?? "Email"}
                      />

                      {/* Direction icon */}
                      <div className="mt-0.5 shrink-0">
                        {message.isInbound ? (
                          <ArrowDownLeft className="h-4 w-4 text-green-500" aria-label="Reçu" />
                        ) : (
                          <ArrowUpRight className="h-4 w-4 text-blue-500" aria-label="Envoyé" />
                        )}
                      </div>

                      {/* Message content */}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-900">
                            {message.from || "Inconnu"}
                          </span>
                          <span className="text-xs text-gray-400">→ {message.to?.join(", ")}</span>
                          {message.hasAttachments ? <Paperclip className="h-3 w-3 text-gray-400" /> : null}
                        </div>
                        <div className="text-sm text-gray-700">{message.subject || "(pas de sujet)"}</div>
                        <div className="text-xs text-gray-400">{truncate(message.text)}</div>
                      </div>

                      {/* Date */}
                      <span className="shrink-0 text-xs text-gray-400">{formatDate(message.date)}</span>
                    </div>
                  </li>
                );
              })}
            </ul>

            {/* Empty state */}
            {!messages.length ? (
              <div className="py-16 text-center text-sm text-gray-500">
                Aucun email trouvé pour ce dossier.
              </div>
            ) : null}
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between rounded-b-xl border-t bg-gray-50 px-6 py-4">
          <span className="text-sm text-gray-500">
            {selectedIds.size} email{selectedIds.size > 1 ? "s" : ""} sélectionné
            {selectedIds.size > 1 ? "s" : ""}
          </span>
          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose}>
              Annuler
            </Button>
            <Button onClick={extract} disabled={selectedIds.size === 0 || extractMutation.isPending}>
              <Mail />
              Extraire la conversation
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
